export const CLIENT_TYPE = {
  WEB: 'WEB',
  NATIVE_IOS: 'IOS',
  NATIVE_ANDROID: 'ANDROID',
} as const;
export type ClientType = typeof CLIENT_TYPE[keyof typeof CLIENT_TYPE];

const isPushApiSupported = (): boolean => {
  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  console.debug('Push API supported:', isSupported);
  return isSupported;
};

const isNotificationApiSupported = (): boolean => {
  const isSupported = 'Notification' in window;
  console.debug('Notification API supported:', isSupported);
  return isSupported;
};

const isIndexedDBSupported = (): boolean => {
  const isSupported = 'indexedDB' in window;
  console.debug('IndexedDB supported:', isSupported);
  return isSupported;
};

const isWebPushSupported = (): boolean => {
  const pushApiSupported = isPushApiSupported();
  const notificationApiSupported = isNotificationApiSupported();
  const indexedDBSupported = isIndexedDBSupported();

  return pushApiSupported && notificationApiSupported && indexedDBSupported;
};

const getWebPushNotificationPermission = (): NotificationPermission | null => {
  if (!isNotificationApiSupported()) {
    throw new Error('Notification API is not supported in this browser');
  }

  const permission = Notification.permission;
  console.debug('Current web push notification permission:', permission);
  return permission;
};

const PERMISSION_TIMEOUT_MS = 20000;

const promptForWebPushNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!isNotificationApiSupported()) {
    throw new Error('Notification API is not supported in this browser');
  }

  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  try {
    console.debug('Requesting web push notification permission');

    const timeoutPromise = new Promise<NotificationPermission>(resolve => {
      timeoutId = setTimeout(() => {
        console.debug(
          'Web push notification permission request timed out after 30 seconds, treating as opt-out',
        );
        resolve('denied');
      }, PERMISSION_TIMEOUT_MS);
    });

    const permission = await Promise.race([Notification.requestPermission(), timeoutPromise]);

    console.debug('Web push notification permission result:', permission);
    return permission;
  } catch (error) {
    console.error('Error requesting web push notification permission:', error);
    throw error;
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  }
};

const getClientType = (): ClientType => {
  const userAgent = navigator.userAgent || (window as any).opera;

  // Android WebView includes 'wv' in the user agent string
  if (/Android/.test(userAgent) && /wv/.test(userAgent)) {
    console.debug('Client type detected: ANDROID webview');
    return CLIENT_TYPE.NATIVE_ANDROID;
  }

  // iOS WebView doesn't have Safari in the user agent or has specific webview indicators
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  if (isIOS) {
    // In iOS, Safari browser has 'Safari' in user agent, but WebView doesn't
    const hasSafari = /Safari/.test(userAgent);
    const hasWebKit = /AppleWebKit/.test(userAgent);
    const isWebView = hasWebKit && !hasSafari;

    if (isWebView) {
      console.debug('Client type detected: IOS webview');
      return CLIENT_TYPE.NATIVE_IOS;
    }
  }

  console.debug('Client type detected: WEB (browser/PWA)');
  return CLIENT_TYPE.WEB;
};

const webPermissionsService = {
  isWebPushSupported,
  isIndexedDBSupported,
  getWebPushNotificationPermission,
  promptForWebPushNotificationPermission,
  getClientType,
};

export default webPermissionsService;
